import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Paper,
  Box,
  Tab,
  Tabs,
  CircularProgress,
  makeStyles,
  Tooltip
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  Assignment as AssignmentIcon,
  CheckCircle as ResolveIcon,
  TrendingUp as EscalateIcon,
  AttachFile as AttachFileIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon
} from '@material-ui/icons';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
  content: {
    padding: theme.spacing(3),
    minHeight: '400px',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  chip: {
    margin: theme.spacing(0.5),
    fontWeight: 'bold',
  },
  notesList: {
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  noteItem: {
    backgroundColor: 'white',
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  infoGrid: {
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  timeline: {
    position: 'relative',
    padding: theme.spacing(2),
  },
  timelineItem: {
    position: 'relative',
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 9,
      top: 0,
      height: '100%',
      width: 2,
      backgroundColor: theme.palette.grey[300],
    },
    '&:last-child:before': {
      display: 'none',
    },
  },
  timelineDot: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    border: `3px solid white`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
}));

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const CustomerComplaintDetailModal = ({
  open,
  onClose,
  complaintId,
  url,
  user,
  onUpdate,
  notification
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [complaint, setComplaint] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [newInternalNote, setNewInternalNote] = useState('');
  const [editedComplaint, setEditedComplaint] = useState({});
  const [users, setUsers] = useState([]);

  const priorityColors = {
    low: '#4caf50',
    medium: '#ff9800',
    high: '#f44336',
    urgent: '#d32f2f',
  };

  const statusColors = {
    open: '#2196f3',
    in_progress: '#ff9800',
    resolved: '#4caf50',
    closed: '#9e9e9e',
    escalated: '#f44336',
  };

  useEffect(() => {
    if (open && complaintId) {
      fetchComplaintDetails();
      fetchUsers();
    }
  }, [open, complaintId]);

  const fetchComplaintDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/customer/complaints/${complaintId}`);
      if (response.data.success) {
        setComplaint(response.data.complaint);
        setEditedComplaint(response.data.complaint);
      }
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load feedback details'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${url}/api/user/users`);
      if (response.data) {
        setUsers(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${url}/api/customer/complaints/${complaintId}`,
        editedComplaint
      );
      
      if (response.data.success) {
        setComplaint(response.data.complaint);
        setEditMode(false);
        notification({
          type: 'success',
          title: 'Success',
          message: 'Feedback updated successfully'
        });
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error updating complaint:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to update feedback'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddFollowUpNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${url}/api/customer/complaints/${complaintId}/followup`,
        { note: newNote }
      );
      
      if (response.data.success) {
        setComplaint(response.data.complaint);
        setNewNote('');
        notification({
          type: 'success',
          title: 'Success',
          message: 'Follow-up note added'
        });
      }
    } catch (error) {
      console.error('Error adding follow-up note:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add follow-up note'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddInternalNote = async () => {
    if (!newInternalNote.trim()) return;
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${url}/api/customer/complaints/${complaintId}/internal-note`,
        { note: newInternalNote }
      );
      
      if (response.data.success) {
        setComplaint(response.data.complaint);
        setNewInternalNote('');
        notification({
          type: 'success',
          title: 'Success',
          message: 'Internal note added'
        });
      }
    } catch (error) {
      console.error('Error adding internal note:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add internal note'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    const resolution = prompt('Please provide resolution details:');
    if (!resolution) return;
    
    try {
      setLoading(true);
      const response = await axios.put(
        `${url}/api/customer/complaints/${complaintId}`,
        { status: 'resolved', resolution }
      );
      
      if (response.data.success) {
        setComplaint(response.data.complaint);
        notification({
          type: 'success',
          title: 'Success',
          message: 'Feedback resolved successfully'
        });
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error resolving complaint:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to resolve feedback'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    const reason = prompt('Please provide escalation reason:');
    if (!reason) return;
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${url}/api/customer/complaints/${complaintId}/escalate`,
        { reason }
      );
      
      if (response.data.success) {
        setComplaint(response.data.complaint);
        notification({
          type: 'success',
          title: 'Success',
          message: 'Feedback escalated successfully'
        });
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error escalating complaint:', error);
      notification({
        type: 'error',
        title: 'Error',
        message: 'Failed to escalate feedback'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !complaint) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent style={{ textAlign: 'center', padding: 50 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!complaint) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">
          Feedback Details - {complaint.clientName}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Overview" />
          <Tab label="Notes & Communication" />
          <Tab label="History" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Overview Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" gap={1}>
                  <Chip
                    label={complaint.status.replace('_', ' ').toUpperCase()}
                    className={classes.chip}
                    style={{ 
                      backgroundColor: statusColors[complaint.status],
                      color: 'white'
                    }}
                  />
                  <Chip
                    label={complaint.priority.toUpperCase()}
                    className={classes.chip}
                    style={{ 
                      backgroundColor: priorityColors[complaint.priority],
                      color: 'white'
                    }}
                  />
                  <Chip
                    label={complaint.complaintType}
                    className={classes.chip}
                  />
                </Box>
                {!editMode ? (
                  <IconButton onClick={() => setEditMode(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                ) : (
                  <Box>
                    <IconButton onClick={handleSave} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton onClick={() => setEditMode(false)}>
                      <CancelIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper className={classes.section} style={{ padding: 16 }}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Client Information
                </Typography>
                <Typography><strong>Client:</strong> {complaint.clientName}</Typography>
                {complaint.jobsiteName && (
                  <Typography><strong>Jobsite:</strong> {complaint.jobsiteName}</Typography>
                )}
                {complaint.jobsiteAddress && (
                  <Typography><strong>Address:</strong> {complaint.jobsiteAddress}</Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper className={classes.section} style={{ padding: 16 }}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Contact Information
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon />
                  <Typography>{complaint.contactName}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon />
                  <Typography>{complaint.contactPhone}</Typography>
                </Box>
                {complaint.contactEmail && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon />
                    <Typography>{complaint.contactEmail}</Typography>
                  </Box>
                )}
                <Typography>
                  <strong>Preferred Contact:</strong> {complaint.preferredContactMethod}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper className={classes.section} style={{ padding: 16 }}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Description
                </Typography>
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editedComplaint.description}
                    onChange={(e) => setEditedComplaint({
                      ...editedComplaint,
                      description: e.target.value
                    })}
                  />
                ) : (
                  <Typography style={{ whiteSpace: 'pre-wrap' }}>
                    {complaint.description}
                  </Typography>
                )}
              </Paper>
            </Grid>

            {editMode && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editedComplaint.status}
                        onChange={(e) => setEditedComplaint({
                          ...editedComplaint,
                          status: e.target.value
                        })}
                        label="Status"
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                        <MenuItem value="escalated">Escalated</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={editedComplaint.priority}
                        onChange={(e) => setEditedComplaint({
                          ...editedComplaint,
                          priority: e.target.value
                        })}
                        label="Priority"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Assigned To</InputLabel>
                      <Select
                        value={editedComplaint.assignedTo?._id || ''}
                        onChange={(e) => setEditedComplaint({
                          ...editedComplaint,
                          assignedTo: e.target.value
                        })}
                        label="Assigned To"
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {users.map(user => (
                          <MenuItem key={user._id} value={user._id}>
                            {user.displayName || user.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {complaint.resolution && (
              <Grid item xs={12}>
                <Paper className={classes.section} style={{ padding: 16 }}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Resolution
                  </Typography>
                  <Typography>{complaint.resolution}</Typography>
                  {complaint.resolvedBy && (
                    <Typography variant="caption">
                      Resolved by {complaint.resolvedBy.displayName} on{' '}
                      {moment(complaint.resolvedAt).format('MM/DD/YYYY hh:mm A')}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box className={classes.actionButtons}>
                {complaint.status !== 'resolved' && complaint.status !== 'closed' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ResolveIcon />}
                    onClick={handleResolve}
                  >
                    Resolve
                  </Button>
                )}
                {complaint.status !== 'escalated' && (
                  <Button
                    variant="contained"
                    style={{ backgroundColor: '#ff9800', color: 'white' }}
                    startIcon={<EscalateIcon />}
                    onClick={handleEscalate}
                  >
                    Escalate
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Notes & Communication Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Follow-up Notes
              </Typography>
              <Paper className={classes.notesList}>
                {complaint.followUpNotes?.length > 0 ? (
                  complaint.followUpNotes.map((note, index) => (
                    <Paper key={index} className={classes.noteItem} style={{ padding: 12 }}>
                      <Typography variant="body2">{note.note}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {note.createdBy?.displayName} - {moment(note.createdAt).format('MM/DD/YY hh:mm A')}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No follow-up notes yet
                  </Typography>
                )}
              </Paper>
              <Box display="flex" gap={1} marginTop={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Add follow-up note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddFollowUpNote}
                  disabled={!newNote.trim()}
                >
                  Add
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Internal Notes (Staff Only)
              </Typography>
              <Paper className={classes.notesList}>
                {complaint.internalNotes?.length > 0 ? (
                  complaint.internalNotes.map((note, index) => (
                    <Paper key={index} className={classes.noteItem} style={{ padding: 12 }}>
                      <Typography variant="body2">{note.note}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {note.createdBy?.displayName} - {moment(note.createdAt).format('MM/DD/YY hh:mm A')}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No internal notes yet
                  </Typography>
                )}
              </Paper>
              <Box display="flex" gap={1} marginTop={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Add internal note..."
                  value={newInternalNote}
                  onChange={(e) => setNewInternalNote(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddInternalNote}
                  disabled={!newInternalNote.trim()}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* History Tab */}
          <Typography variant="h6" className={classes.sectionTitle}>
            Feedback Timeline
          </Typography>
          <div className={classes.timeline}>
            <div className={classes.timelineItem}>
              <div className={classes.timelineDot} />
              <Typography variant="subtitle2">Feedback Created</Typography>
              <Typography variant="body2">
                {moment(complaint.dateReported).format('MM/DD/YYYY hh:mm A')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Reported by {complaint.reportedBy}
              </Typography>
            </div>
            
            {complaint.assignedAt && (
              <div className={classes.timelineItem}>
                <div className={classes.timelineDot} style={{ backgroundColor: '#ff9800' }} />
                <Typography variant="subtitle2">Assigned</Typography>
                <Typography variant="body2">
                  {moment(complaint.assignedAt).format('MM/DD/YYYY hh:mm A')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Assigned to {complaint.assignedTo?.displayName}
                </Typography>
              </div>
            )}
            
            {complaint.resolvedAt && (
              <div className={classes.timelineItem}>
                <div className={classes.timelineDot} style={{ backgroundColor: '#4caf50' }} />
                <Typography variant="subtitle2">Resolved</Typography>
                <Typography variant="body2">
                  {moment(complaint.resolvedAt).format('MM/DD/YYYY hh:mm A')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Resolved by {complaint.resolvedBy?.displayName}
                </Typography>
              </div>
            )}
          </div>
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  notification: state.notification
});

export default connect(mapStateToProps)(CustomerComplaintDetailModal);